import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

export const EMBEDDED_RESIZE_MESSAGE_TYPE = '__tkt_workflow_message__';

/**
 * Post iframe height to the parent when Kronos runs embedded (WP shortcodes).
 * Reuses the workflow postMessage type so existing WP runners keep working.
 *
 * Observes the container with ResizeObserver so height updates on wizard steps,
 * login/register validation messages, and other in-page layout changes — not
 * only when React deps change.
 *
 * @param {React.RefObject<HTMLElement>} containerRef
 * @param {unknown[]} [deps] Extra deps that should re-measure (loading, content).
 */
export function useEmbeddedResize(containerRef, deps = []) {
    const { isEmbedded } = useSelector((state) => state.ui);
    const hasSentFirstMessageRef = useRef(false);
    const rafScheduleRef = useRef(null);

    const handleWindowResize = useCallback(() => {
        if (!isEmbedded) {
            return false;
        }

        const container = containerRef?.current;
        if (!container) {
            return false;
        }

        const rect = container.getBoundingClientRect();
        const width = rect?.width || container.offsetWidth || container.scrollWidth || 0;
        const height = Math.max(
            rect?.height || 0,
            container.offsetHeight || 0,
            container.scrollHeight || 0
        );

        if (height <= 0) {
            return false;
        }

        window.parent.postMessage({
            type: EMBEDDED_RESIZE_MESSAGE_TYPE,
            action: 'resize',
            params: { width, height },
        }, '*');
        hasSentFirstMessageRef.current = true;
        return true;
    }, [containerRef, isEmbedded]);

    const scheduleResize = useCallback(() => {
        if (rafScheduleRef.current != null) {
            return;
        }
        rafScheduleRef.current = window.requestAnimationFrame(() => {
            rafScheduleRef.current = null;
            handleWindowResize();
        });
    }, [handleWindowResize]);

    useEffect(() => {
        hasSentFirstMessageRef.current = false;
        let frameId = null;
        const sendFirstResizeMessage = () => {
            if (hasSentFirstMessageRef.current) {
                return;
            }
            const hasSent = handleWindowResize();
            if (!hasSent) {
                frameId = window.requestAnimationFrame(sendFirstResizeMessage);
            }
        };

        handleWindowResize();
        sendFirstResizeMessage();
        window.addEventListener('resize', handleWindowResize);

        let resizeObserver = null;
        const container = containerRef?.current;
        if (container && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                scheduleResize();
            });
            resizeObserver.observe(container);
        }

        return () => {
            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }
            if (rafScheduleRef.current != null) {
                window.cancelAnimationFrame(rafScheduleRef.current);
                rafScheduleRef.current = null;
            }
            window.removeEventListener('resize', handleWindowResize);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleWindowResize, scheduleResize, ...deps]);
}

export default useEmbeddedResize;
